import React, { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { FaCamera } from 'react-icons/fa';
import { useOrganization } from 'hooks/useOrganization';
import { reactLocalStorage } from 'reactjs-localstorage';
import theme from '../../themes/default';
import Loader from 'react-loader-spinner';
import { useAlert } from 'react-alert';
import {
  IMAGE_REGEX as regex,
  IMAGE_MAX_SIZE as maxSize,
} from 'utils/constants';
import { Tooltip, Permission } from 'components';

const OrgImage = ({ logo, nologo, orgId }) => {
  const [image, setImage] = useState();
  const [error, setError] = useState([]);
  const inputFile = useRef(null);
  const {
    dataUpdate,
    loadingUpdate,
    errorUpdate,
    executeUpdate,
    setId,
  } = useOrganization();
  const alert = useAlert();

  useEffect(() => {
    if (logo) setImage(logo);
    if (orgId) setId(orgId);
  }, [logo, orgId]);

  useEffect(() => {
    if (dataUpdate) {
      alert.success('Image Uploaded Succesfully!');
    }
  }, [dataUpdate]);

  const handleChange = (event) => {
    const reader = new FileReader();
    const file = event.target.files[0];
    const errors = [];
    //Clean past errors
    setError(errors);

    //Validate the extension
    if (!regex.test(file.name)) {
      errors.push(`${file.name} is not an accepted file type.`);
    }

    //Validate the size
    if (maxSize > 0 && file.size > maxSize) {
      const ksize = Math.floor(maxSize / 1024);
      errors.push(
        `${file.name} cannot be used because its larger than ${ksize}Kb. Only files <= ${ksize}Kb are allowed`
      );
    }

    //Show the errors
    if (errors.length) {
      setError(errors);
      return;
    }

    let imageData;

    reader.onload = function () {
      imageData = reader.result;
      setImage(imageData);
      //Make the request
      executeUpdate({
        data: { organization: { logo: imageData } },
      });

      //Update the local storage
      const user = reactLocalStorage.getObject('user');
      const newOrg = { ...user.organization, logo: imageData };
      reactLocalStorage.setObject('user', { ...user, organization: newOrg });
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      {loadingUpdate ? (
        <Spinner>
          <Loader type="Oval" height={30} color={theme.palette.primary[0]} />
        </Spinner>
      ) : (
        <ImageContainer nologo={image ? false : true}>
          <img src={image || nologo} alt="logo" className="img-fluid" />
          <InputFile
            type="file"
            onChange={handleChange}
            ref={inputFile}
            accept="image/*"
          />
          <Permission permission={['canEditOrgs']}>
            <Tooltip text="Upload Image">
              <Upload onClick={() => inputFile.current.click()} />
            </Tooltip>
          </Permission>
          <ul>
            {error.map((err, index) => (
              <li key={index}>{err}</li>
            ))}
          </ul>
        </ImageContainer>
      )}
    </>
  );
};

const Spinner = styled.div`
  text-align: center;
`;

const Upload = styled(FaCamera)`
  margin-top: 0.5rem;
  font-size: 2rem;
  color: ${(props) => props.theme.palette.grayscale[5]};
  transition: color 0.5s ease;
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.palette.primary[1]};
  }
`;

const noLogoCamera = css`
  margin-bottom: 0;
  svg {
    top: 72%;
  }
`;

const ImageContainer = styled.div`
  max-width: 200px;
  text-align: center;
  position: relative;
  margin-bottom: 3rem;

  svg {
    position: absolute;
    top: 100%;
    left: 84px;
    /* //TODO: remove on the future */
    display: none;
  }

  ${(props) => props?.nologo && noLogoCamera}
`;

const InputFile = styled.input`
  display: none;
`;

export default OrgImage;
